"use client";

/**
 * BYOK key entry form (client-only pivot), shared by the header key
 * popover and the staged "connect & transcribe" card.
 *
 * The key is validated against the endpoint from the browser, then held
 * client-side only (lib/client/openai-key.ts). Write-only input — an
 * existing key is never echoed back.
 */
import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DEFAULT_OPENAI_BASE_URL,
  storeOpenAIKey,
  validateOpenAIKey,
} from "@/lib/client/openai-key";

interface OpenAIKeyFormProps {
  /** Submit button label (e.g. "Save key", "Connect & transcribe"). */
  submitLabel: string;
  /** Called after the key was validated and stored. */
  onSaved?: () => void;
  /** Rendered beside the submit button (e.g. a "Not now" escape hatch). */
  secondaryAction?: ReactNode;
  autoFocus?: boolean;
}

export function OpenAIKeyForm({
  submitLabel,
  onSaved,
  secondaryAction,
  autoFocus,
}: OpenAIKeyFormProps) {
  const t = useTranslations("Flow.key");
  const [keyInput, setKeyInput] = useState("");
  const [baseUrlInput, setBaseUrlInput] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [remember, setRemember] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    const key = keyInput.trim();
    const baseUrl = baseUrlInput.trim();
    const usesDefaultEndpoint = baseUrl === "";

    if (usesDefaultEndpoint && (!key.startsWith("sk-") || key.length < 20)) {
      setError(t("errorFormat"));
      return;
    }
    if (!usesDefaultEndpoint && key.length === 0) {
      setError(t("errorEmpty"));
      return;
    }

    setSaving(true);
    const result = await validateOpenAIKey(key, baseUrl || undefined);
    setSaving(false);

    if (!result.ok) {
      setError(
        result.reason === "unauthorized" ? t("errorRejected") : t("errorNetwork"),
      );
      return;
    }

    storeOpenAIKey(key, { remember, baseUrl: baseUrl || undefined });
    setKeyInput("");
    onSaved?.();
  }

  return (
    <form onSubmit={handleSave} className="space-y-3.5 text-left">
      <div className="space-y-1.5">
        <Label htmlFor="openai-key" className="text-[12.5px] font-semibold">
          {t("keyLabel")}
        </Label>
        <Input
          id="openai-key"
          type="password"
          value={keyInput}
          onChange={(e) => {
            setKeyInput(e.target.value);
            setError(null);
          }}
          placeholder="sk-…"
          autoComplete="off"
          autoFocus={autoFocus}
          required
          className="h-10 rounded-[9px] bg-background font-mono text-[13.5px] focus-visible:ring-1 focus-visible:ring-offset-0"
        />
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="remember-key"
          checked={remember}
          onCheckedChange={(checked) => setRemember(checked === true)}
          className="h-[17px] w-[17px] rounded-[4px] border-[1.5px] border-input data-[state=checked]:border-primary"
        />
        <Label htmlFor="remember-key" className="cursor-pointer text-[13px] font-normal">
          {t("remember")}
        </Label>
      </div>

      {showAdvanced ? (
        <div className="space-y-1.5">
          <Label htmlFor="base-url" className="text-[12.5px] font-semibold">
            {t("baseUrlLabel")}
          </Label>
          <Input
            id="base-url"
            type="url"
            value={baseUrlInput}
            onChange={(e) => setBaseUrlInput(e.target.value)}
            placeholder={DEFAULT_OPENAI_BASE_URL}
            className="h-10 rounded-[9px] bg-background font-mono text-[13px] focus-visible:ring-1 focus-visible:ring-offset-0"
          />
        </div>
      ) : (
        <button
          type="button"
          className="text-xs text-muted-foreground underline underline-offset-[3px] hover:text-foreground"
          onClick={() => setShowAdvanced(true)}
        >
          {t("customEndpoint")}
        </button>
      )}

      {error && (
        <p className="text-[13px] text-primary" role="alert">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2.5 pt-1.5">
        <Button
          type="submit"
          disabled={saving}
          className="h-11 min-w-0 flex-1 whitespace-nowrap rounded-[10px] text-sm font-semibold"
        >
          {saving ? t("checking") : submitLabel}
        </Button>
        {secondaryAction}
      </div>
    </form>
  );
}
